// frontend/src/pages/iqgames/fanratings/useFanRatingsHook.js
import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../../hooks/useFetch";

const useFanRatingsHook = () => {
  const authState = useSelector((state) => state.authReducer);
  const [fetchData] = useFetch();

  const userId = authState?.user?._id;
  const userCode = authState?.user?.think5whyscode;
  const userName = authState?.user?.name;
  const token = authState?.token;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [myRatings, setMyRatings] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [result, setResult] = useState(null);

  const apiCall = useCallback(async ({ url, method = "get", data }) => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchData(
        {
          url,
          method,
          data,
          headers: {
            Authorization: token,
          },
        },
        { silent: true }
      );
      return res;
    } catch (err) {
      setError(err?.message || "FanRatings API Error");
      console.error("FanRatings API Error:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchData, token]);

  const getCategories = useCallback(async () => {
    const res = await apiCall({
      url: "/fanratings/categories",
    });
    if (res?.success) {
      setCategories(res.data || []);
    }
    return res;
  }, [apiCall]);

  const getQuestionsByCategory = useCallback(async (category = "") => {
    const url = category
      ? `/fanratings/questions?category=${category}`
      : "/fanratings/questions";

    const res = await apiCall({
      url,
      method: "get",
    });

    if (res?.success) {
      setQuestions(res.data || []);
    }

    return res;
  }, [apiCall]);

  const getQuestionById = useCallback(async (questionId) => {
    return await apiCall({
      url: `/fanratings/question/${questionId}`,
    });

  }, [apiCall]);

  const submitRating = useCallback(async ({ questionId, userResponse }) => {
    if (!userCode) return;

    return await apiCall({
      url: "/fanratings/submit",
      method: "post",
      data: {
        questionId,
        userId,
        userCode,
        userName,
        userResponse,
      },
    });
  }, [apiCall, userId, userCode, userName]);

  const getMyRatings = useCallback(async () => {
    const res = await apiCall({
      url: `/fanratings/my-ratings?userCode=${userCode}`,
    });
    if (res?.success) {
      setMyRatings(res.data || []);
    }
    return res;
  }, [apiCall, userCode]);

  const getRankings = useCallback(async (questionId) => { 
    const res = await apiCall({
      url: `/fanratings/rankings/${questionId}`,
    });
    if (res?.success) {
      setRankings(res.data || []);
    }
    return res;
  }, [apiCall]);

  const getResult = useCallback(async (questionId) => {
    const res = await apiCall({
      url: `/fanratings/result/${questionId}`,
    });
    if (res?.success) {
      setResult(res.data || null);
    }
    return res;
  }, [apiCall]);

  return {
    loading,
    error,
    questions,
    categories,
    myRatings,
    rankings,
    result,
    getCategories,
    getQuestionsByCategory,
    getQuestionById,
    submitRating,
    getMyRatings,
    getRankings,
    getResult,
  };
};

export default useFanRatingsHook;